<template>
  <div class="cabadmin-usage">
    <!-- Navigation mois -->
    <div class="month-nav">
      <button class="btn btn-outline nav-arrow" @click="prevMonth" title="Mois précédent">‹</button>
      <span class="month-label">{{ monthLabel }}</span>
      <button class="btn btn-outline nav-arrow" :disabled="isCurrentMonth" @click="nextMonth" title="Mois suivant">›</button>
      <button
        class="btn btn-export"
        :disabled="exporting"
        title="Exporter l'activité du mois en Excel"
        @click="exportExcel"
      >{{ exporting ? 'Export…' : 'Exporter en Excel' }}</button>
    </div>

    <div v-if="loading" class="state-block">Chargement…</div>
    <div v-else-if="error" class="state-block error">{{ error }}</div>

    <template v-else-if="usage">
      <!-- KPIs -->
      <div class="kpi-row">
        <div class="kpi-card">
          <div class="kpi-value">{{ usage.totals.prompts }}</div>
          <div class="kpi-label">Recherches</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-value">{{ usage.totals.active_users }}<span class="kpi-sub">/ {{ usage.members }}</span></div>
          <div class="kpi-label">Utilisateurs actifs</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-value">{{ usage.totals.avg_per_user }}</div>
          <div class="kpi-label">Recherches / utilisateur actif</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-value">{{ usage.totals.success_rate === null ? '—' : usage.totals.success_rate + '%' }}</div>
          <div class="kpi-label">Taux de succès</div>
        </div>
      </div>

      <!-- Graphe quotidien -->
      <div class="panel">
        <h3 class="panel-title">Recherches par jour</h3>
        <div v-if="usage.totals.prompts === 0" class="state-block">Aucune activité ce mois-ci.</div>
        <div v-else class="bars">
          <div
            v-for="d in usage.days"
            :key="d.date"
            class="bar-col"
            :title="`${formatDay(d.date)} — ${d.count} recherche${d.count > 1 ? 's' : ''}`"
          >
            <span v-if="d.count" class="bar-count">{{ d.count }}</span>
            <div class="bar" :style="{ height: barHeight(d.count) }"></div>
            <span class="bar-day">{{ d.date.slice(8, 10) }}</span>
          </div>
        </div>
      </div>

      <!-- Activité live -->
      <div class="panel">
        <div class="panel-head">
          <h3 class="panel-title">Activité live</h3>
          <div class="feed-filter-wrap">
            <input
              v-model="feedEmail"
              list="cabadmin-member-emails"
              type="text"
              class="form-input feed-filter"
              placeholder="Filtrer par email…"
              @change="applyFeedEmail"
              @keyup.enter="applyFeedEmail"
            />
            <datalist id="cabadmin-member-emails">
              <option v-for="m in members" :key="m" :value="m" />
            </datalist>
            <button v-if="feedEmailApplied" class="btn btn-outline btn-sm" @click="clearFeedEmail">
              ✕ {{ feedEmailApplied }}
            </button>
          </div>
        </div>

        <div v-if="feedLoading" class="state-block">Chargement…</div>
        <div v-else-if="!feedItems.length" class="state-block">
          {{ feedEmailApplied ? 'Aucune interaction pour cet utilisateur.' : 'Aucune interaction récente.' }}
        </div>
        <template v-else>
          <table class="usage-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Utilisateur</th>
                <th>Mode</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(it, i) in visibleFeed" :key="i">
                <td class="muted">{{ formatDateTime(it.created_at) }}</td>
                <td class="user-email">{{ it.email }}</td>
                <td><span class="mode-badge" :class="'mode-' + it.mode">{{ modeLabel(it.mode) }}</span></td>
                <td>
                  <span class="status-dot" :class="'st-' + statusKind(it.status)"></span>
                  {{ statusLabel(it.status) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div class="feed-footer">
            <span class="feed-count">{{ visibleFeed.length }} sur {{ feedItems.length }}</span>
            <div class="feed-steps">
              <span class="feed-steps-label">Afficher</span>
              <button
                v-for="n in feedSteps"
                :key="n"
                class="step-chip"
                :class="{ active: feedLimit === n }"
                @click="feedLimit = n"
              >{{ n }}</button>
            </div>
          </div>
        </template>
      </div>

      <!-- Détail par utilisateur -->
      <div class="panel">
        <h3 class="panel-title">Détail par utilisateur</h3>
        <table class="usage-table">
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th class="num">Recherches</th>
              <th>Dernière activité</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!usage.users.length"><td colspan="3" class="state-row">Aucun utilisateur actif ce mois-ci.</td></tr>
            <tr v-for="u in usage.users" :key="u.user_id">
              <td class="user-email">{{ u.email }}</td>
              <td class="num">{{ u.prompts }}</td>
              <td class="muted">{{ formatDay(u.last_activity) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { getUsage, getActivity, exportUsage } from '@/services/cabadmin';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function currentYM() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export default {
  name: 'CabAdminUsage',
  props: {
    cabinet: { type: Object, default: null }
  },
  data() {
    return {
      month: currentYM(),
      usage: null,
      loading: false,
      error: null,
      exporting: false,
      // Activité live (indépendante du mois)
      feedItems: [],
      members: [],
      feedLoading: false,
      feedLimit: 5,
      feedSteps: [5, 10, 15, 25, 50],
      feedEmail: '',
      feedEmailApplied: ''
    };
  },
  computed: {
    monthLabel() {
      const [y, m] = this.month.split('-');
      return `${MOIS[parseInt(m, 10) - 1]} ${y}`;
    },
    isCurrentMonth() {
      return this.month >= currentYM();
    },
    maxDayCount() {
      if (!this.usage) return 0;
      return Math.max(...this.usage.days.map(d => d.count), 1);
    },
    visibleFeed() {
      return this.feedItems.slice(0, this.feedLimit);
    }
  },
  async mounted() {
    await Promise.all([this.reload(), this.loadFeed()]);
  },
  methods: {
    shiftMonth(delta) {
      const [y, m] = this.month.split('-').map(Number);
      const d = new Date(y, m - 1 + delta, 1);
      this.month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      this.reload();
    },
    prevMonth() { this.shiftMonth(-1); },
    nextMonth() { if (!this.isCurrentMonth) this.shiftMonth(1); },
    barHeight(count) {
      if (!count) return '2px';
      return Math.max(6, Math.round(100 * count / this.maxDayCount)) + 'px';
    },
    formatDay(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (_) {
        return iso.slice(0, 10);
      }
    },
    formatDateTime(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleString('fr-FR', {
          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
        });
      } catch (_) {
        return iso.slice(0, 16).replace('T', ' ');
      }
    },
    modeLabel(mode) {
      return {
        flash: 'Réponse rapide',
        deep: 'Réponse longue',
        agent: 'Agent',
        tableau: 'Tableau'
      }[mode] || mode || '—';
    },
    statusKind(status) {
      if (status === 'done') return 'done';
      if (status === 'failed' || status === 'error') return 'failed';
      return 'pending';
    },
    statusLabel(status) {
      return { done: 'Réussi', failed: 'Échoué', pending: 'En cours' }[this.statusKind(status)];
    },
    async reload() {
      this.loading = true;
      this.error = null;
      try {
        this.usage = await getUsage(this.month);
      } catch (e) {
        console.error('[CabAdminUsage] reload failed:', e);
        this.error = "Impossible de charger l'activité du cabinet.";
      } finally {
        this.loading = false;
      }
    },
    async loadFeed() {
      this.feedLoading = true;
      try {
        const data = await getActivity({ email: this.feedEmailApplied, limit: 50 });
        this.feedItems = data.items || [];
        if (data.members && data.members.length) this.members = data.members;
      } catch (e) {
        console.error('[CabAdminUsage] loadFeed failed:', e);
        this.feedItems = [];
      } finally {
        this.feedLoading = false;
      }
    },
    applyFeedEmail() {
      const v = (this.feedEmail || '').trim().toLowerCase();
      if (v === this.feedEmailApplied) return;
      this.feedEmailApplied = v;
      this.feedLimit = 5;
      this.loadFeed();
    },
    clearFeedEmail() {
      this.feedEmail = '';
      this.feedEmailApplied = '';
      this.feedLimit = 5;
      this.loadFeed();
    },
    parseFilename(disposition) {
      if (!disposition) return '';
      // Gère filename="..." et filename*=UTF-8''...
      let m = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
      if (m && m[1]) {
        try { return decodeURIComponent(m[1].replace(/["']/g, '').trim()); } catch (_) { /* noop */ }
      }
      m = /filename="?([^";]+)"?/i.exec(disposition);
      return m && m[1] ? m[1].trim() : '';
    },
    async exportExcel() {
      if (this.exporting) return;
      this.exporting = true;
      try {
        const response = await exportUsage(this.month);
        const filename = this.parseFilename(response.headers['content-disposition'])
          || `usage_${this.month}.xlsx`;
        const blob = new Blob([response.data], {
          type: response.data.type
            || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error('[CabAdminUsage] exportExcel failed:', e);
        this.error = "Impossible d'exporter l'activité du cabinet.";
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.month-nav {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.month-label {
  min-width: 140px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
}
.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;
}
.btn:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-outline { background: white; border: 1px solid #ddd; color: #555; }
.btn-outline:hover:not(:disabled) { border-color: #999; color: #222; }
.nav-arrow { padding: 6px 14px; font-size: 16px; line-height: 1; }
.btn-export {
  margin-left: auto;
  background: #16a34a;
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-export:hover:not(:disabled) { background: #15803d; box-shadow: 0 4px 12px rgba(22, 163, 74, 0.5); }

.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.kpi-card {
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}
.kpi-value { font-size: 26px; font-weight: 700; color: #6c63ff; }
.kpi-sub { font-size: 14px; font-weight: 500; color: #999; margin-left: 4px; }
.kpi-label { margin-top: 4px; font-size: 12px; color: #777; }

.panel {
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}
.panel-title {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 140px;
  padding-bottom: 18px;
  position: relative;
}
.bar-col {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  position: relative;
  min-width: 0;
}
.bar {
  width: 100%;
  max-width: 26px;
  background: #6c63ff;
  border-radius: 3px 3px 0 0;
}
.bar-count { font-size: 10px; color: #555; margin-bottom: 2px; }
.bar-day {
  position: absolute;
  bottom: -18px;
  font-size: 9px;
  color: #999;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.panel-head .panel-title { margin: 0; }
.feed-filter-wrap { display: flex; align-items: center; gap: 8px; }
.form-input {
  padding: 7px 11px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}
.feed-filter { min-width: 240px; }
.btn-sm { padding: 6px 10px; font-size: 12px; }

.mode-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.78em;
  font-weight: 600;
  white-space: nowrap;
  background: #eef2ff;
  color: #4f46e5;
}
.mode-badge.mode-agent { background: #ede9fe; color: #6c63ff; }
.mode-badge.mode-deep { background: #fef3c7; color: #b45309; }
.mode-badge.mode-flash { background: #dbeafe; color: #1d4ed8; }
.status-dot {
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
  background: #9ca3af;
}
.status-dot.st-done { background: #16a34a; }
.status-dot.st-failed { background: #dc2626; }
.status-dot.st-pending { background: #f59e0b; }

.feed-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.feed-count { font-size: 12px; color: #999; }
.feed-steps { display: flex; align-items: center; gap: 4px; }
.feed-steps-label { font-size: 12px; color: #999; margin-right: 4px; }
.step-chip {
  border: 1px solid #ddd;
  background: white;
  color: #555;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}
.step-chip:hover { border-color: #999; }
.step-chip.active { background: #6c63ff; color: white; border-color: #6c63ff; }

.usage-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.usage-table th, .usage-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}
.usage-table th { background: #fafafa; font-weight: 600; color: #555; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; }
.usage-table tbody tr:hover { background: #fafbff; }
.usage-table tbody tr:last-child td { border-bottom: none; }
.usage-table .num { text-align: right; font-variant-numeric: tabular-nums; }
.user-email { font-weight: 500; }
.muted { color: #777; }
.state-row { text-align: center; padding: 24px 16px; color: #999; }
.state-block { padding: 32px 16px; text-align: center; color: #777; }
.state-block.error { color: #c62828; }
</style>
